import React, { FunctionComponent, useEffect, useState, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { map, filter, without, pipe, append, find, sortWith, ascend } from 'ramda';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import { setSelectedQuestionsRoutine } from '../../routines';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { getRemainingQuestions, orderByAttribute, getColumns } from './utils';
import { IAttribute } from '../../../../common/models/attribute/IAttribute';
import { QuestionType } from '../../../../common/enums/QuestionType';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import Table from '../../../../components/Table';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import { QuestionSection } from '../../../../common/enums/QuestionSection';

interface IProps {
  selectedQuestions: ISurveyQuestion[];
  attributes: IAttribute[];
  ratingScaleQuestions: IQuestionWithOwner[];
  questionLoading: boolean;
  setSelectedQuestions: IBindingCallback1<ISurveyQuestion[]>;
  openQuestionBank: IBindingAction;
}

const SoftSkillsTable: FunctionComponent<IProps> = ({
  questionLoading,
  selectedQuestions,
  ratingScaleQuestions,
  setSelectedQuestions,
  openQuestionBank
}) => {
  const rowHeight = 65;
  const shownQuestions = pipe<ISurveyQuestion[], ISurveyQuestion[], ISurveyQuestion[]>(
    filter(({ question }: ISurveyQuestion) => question.type === QuestionType.RatingScale),
    sortWith([orderByAttribute,
      ascend(({ question }) => question.subAttribute?.name)])
  )(selectedQuestions);
  const defaultRemainingQuestions = getRemainingQuestions(ratingScaleQuestions, selectedQuestions);
  const [allRemainingQuestions, setAllRemainingQuestions] = useState(defaultRemainingQuestions);

  useEffect(() => {
    setAllRemainingQuestions(defaultRemainingQuestions);
  }, [defaultRemainingQuestions.length]);

  const onRemoveQuestion = (question: ISurveyQuestion) => {
    const newSelected = without([question], selectedQuestions);
    setSelectedQuestions(newSelected);
  };

  const onReplaceQuestion = (oldQuestion: ISurveyQuestion, remainingQuestions: IQuestionWithOwner[]) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { createdByUser, isUsed, defaultSendTo, ...newQuestion } = remainingQuestions[0];
    const updatedQuestions = map(item => (item.question.id === oldQuestion.question.id
      ? { ...oldQuestion, question: newQuestion }
      : item
    ), selectedQuestions);

    const newRemainingQuestions = pipe(
      without([remainingQuestions[0]]),
      append(find(({ id }) => oldQuestion.question.id === id, ratingScaleQuestions))
    )(allRemainingQuestions) as IQuestionWithOwner[];

    setSelectedQuestions(updatedQuestions);
    setAllRemainingQuestions(newRemainingQuestions);
  };

  const onCellClicked = ({ accessor }: IRowAccessor<ISurveyQuestion>, event: MouseEvent<HTMLElement>) => {
    switch (accessor) {
      case 'actions':
        event.stopPropagation();
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <Table
        config={{
          columns: getColumns(allRemainingQuestions, { onReplace: onReplaceQuestion, onRemove: onRemoveQuestion }),
          data: { items: shownQuestions, total: shownQuestions.length },
          rowHeight,
          autoHeight: true,
          onCellClicked,
          defaultSorted: [{ id: 'competency', desc: false }],
          noDataPlaceholder: <NoDataPlaceholder variant="table" onButtonClick={openQuestionBank} />,
          noDataPlaceholderHeight: 400,
          loading: questionLoading
        }}
      />
    </div>
  );
};

const mapStateToProps = ({
  surveyBuilder: { selectedQuestions, attributes, questions, questionLoading }
}: IAppState) => ({
  selectedQuestions,
  attributes,
  ratingScaleQuestions: questions[QuestionSection.SoftSkills],
  questionLoading
});

const mapDispatchToProps = { setSelectedQuestions: setSelectedQuestionsRoutine };

export default connect(mapStateToProps, mapDispatchToProps)(SoftSkillsTable);
